import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, BookOpen, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageTip } from "@/components/PageTip";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getStudySets, createStudySet } from "@/hooks/useFirestore";
import { useAuth } from "@/contexts/AuthContext";

export function StudySetsPage() {
  const { user } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSetName, setNewSetName] = useState("");
  const [newSetDescription, setNewSetDescription] = useState("");
  const queryClient = useQueryClient();

  const { data: studySets, isLoading } = useQuery({
    queryKey: ["studySets", user?.uid],
    queryFn: () => getStudySets(user!.uid),
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: () => createStudySet(user!.uid, newSetName, newSetDescription),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      setNewSetName("");
      setNewSetDescription("");
      setShowCreateDialog(false);
    },
  });

  const handleCreateStudySet = () => {
    if (newSetName.trim()) {
      createMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // Calculate total words across all study sets
  const totalWords = studySets?.reduce((sum, set) => sum + set.wordIds.length, 0) || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between" data-walkthrough="scenarios-header">
        <div>
          <h1 className="text-2xl font-semibold">Scenarios</h1>
          <p className="text-muted-foreground">
            {studySets?.length || 0} scenarios, {totalWords} total words
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Scenario
        </Button>
      </div>
      <PageTip pageKey="scenarios" message="Prep vocab for real-life situations. Create a scenario like 'Doctor's appointment' or 'Job interview', and AI generates the exact Hebrew words you'll need." />

      {/* Study Set List */}
      <div className="space-y-2">
        {studySets?.length === 0 ? (
          <Card className="p-8">
            <div className="text-center text-muted-foreground">
              <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No scenarios yet</p>
              <p className="text-sm">Create a scenario to prep vocab for real-life situations — doctor visits, job interviews, errands</p>
            </div>
          </Card>
        ) : (
          studySets?.map((studySet) => (
            <Link key={studySet.id} to={`/scenarios/${studySet.id}`}>
              <Card className="p-4 card-hover cursor-pointer">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <span className="font-medium">{studySet.name}</span>
                      {studySet.description && (
                        <p className="text-sm text-muted-foreground">{studySet.description}</p>
                      )}
                    </div>
                    <Badge variant="secondary">{studySet.wordIds.length} words</Badge>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Card>
            </Link>
          ))
        )}
      </div>

      {/* Create Scenario Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Scenario</DialogTitle>
            <DialogDescription>
              Prep vocab for a real-life situation — what's coming up?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={newSetName}
                onChange={(e) => setNewSetName(e.target.value)}
                placeholder="e.g., Doctor's Appointment, Job Interview"
                onKeyDown={(e) => e.key === "Enter" && handleCreateStudySet()}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description (optional)</label>
              <Input
                value={newSetDescription}
                onChange={(e) => setNewSetDescription(e.target.value)}
                placeholder="What's the situation?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateStudySet}
              disabled={!newSetName.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
